import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';

import { showError } from '@utils';
import { boardsServices } from '@services';
import { BOARDS_ID, LIMIT } from '@constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useQuery } from 'react-query';
import { useAssignees } from 'hooks/common';
import {
  filterGreenCardAssignees,
  getGreenCardColumns,
  handleGreenCardData,
  getGreenCardParamsAndPayload,
} from './utils';

export const GREEN_CARD_PAGE_SIZE = LIMIT;

export const useGreenCardOperations = (fetchOnMount) => {
  const router = useRouter();
  const {
    assignees: allAssignees,
    isFetching: isAssigneesFetching,
    assigneesWithNone,
  } = useAssignees();
  const assignees = filterGreenCardAssignees(allAssignees);

  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || GREEN_CARD_PAGE_SIZE;
  const orderBy = router.query?.orderBy || 'createdAt';
  const order = router.query?.order || 'desc';
  const query = router.query?.query;
  const assignee = router.query?.assignee;

  const { isReady } = router;

  const { params, exportParams, payload, requestUrlParams } = useMemo(() => {
    return getGreenCardParamsAndPayload({
      page,
      pageSize: parseInt(pageSize, 10) || GREEN_CARD_PAGE_SIZE,
      orderBy,
      order,
      query,
      assignee,
    });
  }, [page, pageSize, orderBy, order, query, assignee]);

  const greenCardResponse = useQuery(
    [QUERY_KEYS.GET_GREEN_CARD_KEY, params],
    () => {
      return boardsServices.getBoardTasks({
        payload,
        boardId: BOARDS_ID.GREEN_CARD,
        params: requestUrlParams,
      });
    },
    { enabled: isReady && fetchOnMount }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, BOARDS_ID.GREEN_CARD],
    () => boardsServices.getEnumsPerBoardId(BOARDS_ID.GREEN_CARD),
    { enabled: router.isReady }
  );

  useEffect(() => {
    if (greenCardResponse?.error) {
      showError(greenCardResponse.error);
    }
  }, [greenCardResponse?.error]);

  const greenCard = handleGreenCardData(
    greenCardResponse?.data?.data || [],
    assignees
  );
  const columns = getGreenCardColumns(greenCard?.tasks || []);

  return {
    page,
    total: greenCardResponse?.data?.meta?.total,
    pageSize: parseInt(pageSize, 10) || GREEN_CARD_PAGE_SIZE,
    loading: greenCardResponse?.isLoading || isAssigneesFetching,
    tasks: greenCard?.tasks || [],
    columns,
    assignees,
    assigneesWithNone,
    hasErrors: greenCardResponse?.isError,
    errorMessage: greenCardResponse?.error,
    greenCardParams: exportParams,
    greenCardQueryPayload: payload,
    enums: enumsResponse?.data?.data || {},
    getGreenCard: greenCardResponse.refetch,
  };
};
